import { useCallback, useMemo } from 'react'
import { useAuth0 } from '@auth0/auth0-react'

import { useMockAuth0 } from '../devAuth'
import { auth0Claims, defaultOrganization, userRoles } from '../auth/consts'

const RELOGIN_ERROR_CODES = [
  'login_required',
  'consent_required',
  'missing_refresh_token',
  'interaction_required',
]

let isReloginInFlight = false

export const readAuthorization = (user) => {
  if (!user) {
    return { organization: defaultOrganization, roles: [] }
  }

  const consolidated = user[auth0Claims.authorizationClaim]
  if (consolidated && typeof consolidated === 'object' && Array.isArray(consolidated.roles)) {
    return {
      organization: consolidated.organization || defaultOrganization,
      roles: consolidated.roles,
    }
  }

  const legacyRoles = user[auth0Claims.rolesClaim]
  return {
    organization:
      user[auth0Claims.userAuthClaim]?.user_metadata?.Organization || defaultOrganization,
    roles: Array.isArray(legacyRoles) ? legacyRoles : [],
  }
}

const redirectToReLogin = async (auth0) => {
  if (isReloginInFlight) return
  isReloginInFlight = true
  try {
    await auth0.loginWithRedirect({
      appState: { returnTo: window.location.pathname + window.location.search },
    })
  } catch (redirectError) {
    isReloginInFlight = false
    throw redirectError
  }
}

const useAuthz = () => {
  const mockAuth = useMockAuth0()
  const auth0 = useAuth0()
  const activeAuth = mockAuth || auth0
  const { user, isAuthenticated, isLoading, error } = activeAuth

  const { organization, roles } = useMemo(() => readAuthorization(user), [user])

  const isForceUserMode = process.env.REACT_APP_FORCE_USER_MODE === 'true'
  const isAdmin = !isForceUserMode && isAuthenticated && roles.includes(userRoles.ADMINISTRATOR)

  const getAccessToken = useCallback(async () => {
    if (mockAuth) return null

    try {
      const token = await auth0.getAccessTokenSilently()
      isReloginInFlight = false
      return token
    } catch (tokenError) {
      if (!RELOGIN_ERROR_CODES.includes(tokenError.error)) {
        throw tokenError
      }
      await redirectToReLogin(auth0)
      return null
    }
  }, [mockAuth, auth0])

  const logout = useCallback(() => {
    if (mockAuth) {
      mockAuth.logout()
      return
    }
    auth0.logout({ logoutParams: { returnTo: window.location.origin } })
  }, [mockAuth, auth0])

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    organization,
    roles,
    isAdmin,
    getAccessToken,
    logout,
  }
}

export default useAuthz
